/**
 * useRRACScript
 *
 * Single source of truth for loading the ApartmentSync (Repli360 / RRAC) script.
 *
 * One global script element is shared across every caller — the
 * apartment-sync-loader injection block (role: 'owner') and the
 * apartment-sync / apartment-sync-map section blocks (role: 'consumer').
 * Refcounted so the script stays alive while any block needs it and is torn
 * down 100ms after the last unmount (the delay survives StrictMode's
 * synchronous remount, matching the pattern in useScriptedContainer).
 *
 * If a consumer block mounts on a page without the loader, it self-loads as
 * a fallback owner. A second consumer on the same page joins the existing
 * load instead of creating a duplicate — fixing the double-load bug the two
 * blocks had before this hook existed.
 *
 * The RRAC script also handles site-wide phone-number replacement and call
 * tracking, so loading it more than once would double-fire those side
 * effects. This hook guarantees at-most-once loading per page.
 *
 * Reinit: RRAC binds its widget init to `jQuery(window).on('load')`. We
 * request a synthetic load after the script is ready and on later consumer
 * mounts so freshly-mounted containers get picked up. Needed in both modes:
 *   - SSG: script.async=true can finish after the natural window.load.
 *   - SPA: the natural load event already fired before navigation.
 *
 * Synthetic load is skipped before the natural page load and coalesced per
 * tick so the owner + consumer pair does not double-initialize RRAC.
 */

import { useEffect, useState } from 'react';

const SCRIPT_ID = 'apartment-sync-script';
const JQUERY_ID = 'apartment-sync-jquery';
const STYLE_ID = 'apartment-sync-styles';
const BASE_URL = 'https://app.repli360.com/admin/rrac-website-script';
const JQUERY_URL = 'https://code.jquery.com/jquery-1.11.3.min.js';
const REGISTRY_KEY = '__apartment_sync_registry__';
const PUBLIC_KEY = '__apartment_sync__';
const CLEANUP_DELAY_MS = 100;

/**
 * Vendor-script helpers that RRAC appends directly to <body> (modal/popup
 * shell, loader spinner, apartment-details panel, date picker). They live
 * outside our React-controlled container, so React's unmount can't reach
 * them — without explicit cleanup they accumulate across SPA navigations
 * and visibly leak (orphaned overlays / stacked loaders) on the next page.
 *
 * Selectors target the body-level shells; nested helpers come along for
 * free since they're descendants. We restrict removal to direct <body>
 * children so we never eat widget UI nested inside our own mounted div.
 */
const RRAC_BODY_HELPER_SELECTORS = ['.rrac-popup', '.rrac_modal_container'];

function cleanupRRACBodyHelpers(): void {
  for (const sel of RRAC_BODY_HELPER_SELECTORS) {
    document.querySelectorAll(sel).forEach((el) => {
      if (el.parentElement === document.body) el.remove();
    });
  }
}

/**
 * Stamp `dniphone` / `dniphonehref` onto every tel:/sms: anchor so the
 * RRAC script (which keys off these classes) can swap in the tracking
 * number. Themes render phone anchors ad-hoc in many places — a DOM
 * pass + MutationObserver covers them all without any per-theme work.
 *
 * Idempotent: each anchor gets `data-dni-stamped` so re-scans no-op.
 */
const DNI_CLASSES = ['dniphone', 'dniphonehref'] as const;
const DNI_STAMP_ATTR = 'data-dni-stamped';
const DNI_SELECTOR = `a[href^="tel:" i]:not([${DNI_STAMP_ATTR}]), a[href^="sms:" i]:not([${DNI_STAMP_ATTR}])`;
const DNI_HREF_RE = /^(tel:|sms:)/i;

function stampAnchor(a: HTMLAnchorElement): void {
  for (const cls of DNI_CLASSES) {
    if (!a.classList.contains(cls)) a.classList.add(cls);
  }
  a.setAttribute(DNI_STAMP_ATTR, '1');
}

function stampDNIClassesIn(root: ParentNode): void {
  root.querySelectorAll<HTMLAnchorElement>(DNI_SELECTOR).forEach(stampAnchor);
}

function maybeStampNode(node: Node): void {
  if (!(node instanceof Element)) return;
  if (
    node instanceof HTMLAnchorElement &&
    !node.hasAttribute(DNI_STAMP_ATTR) &&
    DNI_HREF_RE.test(node.getAttribute('href') ?? '')
  ) {
    stampAnchor(node);
  }
  // Descendants (added subtrees, e.g., a freshly mounted Footer)
  if ('querySelectorAll' in node) stampDNIClassesIn(node);
}

function startDNIObserver(): MutationObserver {
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'childList') {
        m.addedNodes.forEach(maybeStampNode);
      } else if (
        m.type === 'attributes' &&
        m.target instanceof HTMLAnchorElement &&
        !m.target.hasAttribute(DNI_STAMP_ATTR) &&
        DNI_HREF_RE.test(m.target.getAttribute('href') ?? '')
      ) {
        stampAnchor(m.target);
      }
    }
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['href'],
  });
  return observer;
}

function unstampAllDNIMarkers(): void {
  // Drop the stamp markers so a fresh load re-scans cleanly. We keep the
  // class names themselves — RRAC may still hold references and removing
  // them mid-flight could cause flicker. Re-stamping is a no-op anyway.
  document.querySelectorAll(`[${DNI_STAMP_ATTR}]`).forEach((el) => {
    el.removeAttribute(DNI_STAMP_ATTR);
  });
}

const RRAC_STYLE_OVERRIDES = `
.rrac_script, .rrac_interactive_leasing_view { position: relative; overflow: hidden; background: #ffffff; min-height: 200px; }
.rrac_script .rrac_tabSection, .rrac_interactive_leasing_view .rrac_tabSection { position: relative !important; overflow: hidden; }
.rrac_script .rrac_tabSection::after, .rrac_interactive_leasing_view .rrac_tabSection::after { content: ''; display: table; clear: both; }
.rrac_script .rrac-tab-container, .rrac_interactive_leasing_view .rrac-tab-container { position: relative !important; }
.rrac_script .rrac-tab-container .floorPlanGrid, .rrac_interactive_leasing_view .rrac-tab-container .floorPlanGrid { position: relative !important; }
.rrac_script .floorPlanItem, .rrac_interactive_leasing_view .floorPlanItem { position: relative !important; float: none !important; }
.rrac_script .rrac_loader_container, .rrac_interactive_leasing_view .rrac_loader_container { display: flex !important; justify-content: center !important; align-items: center !important; width: 100% !important; min-height: 120px; padding: 24px 0; background: #ffffff; }
.rrac_script .loadingImage, .rrac_interactive_leasing_view .loadingImage { display: block !important; margin: 40px auto !important; }
`;

type Status = 'idle' | 'loading' | 'ready' | 'error';
type OwnerMode = 'global' | 'fallback';
type Role = 'owner' | 'consumer';

interface RegistryEntry {
  mountCount: number;
  consumerCount: number;
  cleanupTimer: number | null;
  consumerCleanupTimer: number | null;
  scriptEl: HTMLScriptElement | null;
  jqueryEl: HTMLScriptElement | null;
  styleEl: HTMLStyleElement | null;
  ownerJavascriptId: string | null;
  ownerConfigId: string | null;
  ownerMode: OwnerMode;
  status: Status;
  reinitTimer: number | null;
  ready: Promise<void>;
  resolveReady: () => void;
  rejectReady: (err: unknown) => void;
  injectDniClasses: boolean;
  dniObserver: MutationObserver | null;
}

interface PublicGlobal {
  version: 1;
  status: Status;
  javascriptId: string | null;
  configId: string | null;
  ownerMode: OwnerMode;
  ready: Promise<void>;
  reinit: () => void;
}

function getRegistry(): RegistryEntry | null {
  if (typeof window === 'undefined') return null;
  return (window as any)[REGISTRY_KEY] ?? null;
}

function setRegistry(entry: RegistryEntry | null): void {
  if (entry === null) {
    delete (window as any)[REGISTRY_KEY];
  } else {
    (window as any)[REGISTRY_KEY] = entry;
  }
}

function publishGlobal(entry: RegistryEntry | null): void {
  if (entry === null) {
    delete (window as any)[PUBLIC_KEY];
    return;
  }
  const pub: PublicGlobal = {
    version: 1,
    status: entry.status,
    javascriptId: entry.ownerJavascriptId,
    configId: entry.ownerConfigId,
    ownerMode: entry.ownerMode,
    ready: entry.ready,
    reinit: requestReinit,
  };
  (window as any)[PUBLIC_KEY] = pub;
}

function reinit(): void {
  const jq = (window as any).jQuery;
  if (jq) jq(window).trigger('load');
}

function requestReinit(): void {
  const entry = getRegistry();
  if (!entry) {
    reinit();
    return;
  }

  // If RRAC registered before the browser load event, let the real event
  // initialize it once. Triggering here would duplicate template-render/dni-fetch.
  if (document.readyState !== 'complete') return;
  if (entry.reinitTimer !== null) return;

  entry.reinitTimer = window.setTimeout(() => {
    const latest = getRegistry();
    if (!latest) return;
    latest.reinitTimer = null;
    reinit();
  }, 0);
}

function createRegistry(
  javascriptId: string,
  configId: string | null,
  ownerMode: OwnerMode,
  injectDniClasses: boolean,
): RegistryEntry {
  let resolveReady!: () => void;
  let rejectReady!: (err: unknown) => void;
  const ready = new Promise<void>((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });
  // Don't crash on unhandled rejection if no caller awaits.
  ready.catch(() => undefined);

  const entry: RegistryEntry = {
    mountCount: 0,
    consumerCount: 0,
    cleanupTimer: null,
    consumerCleanupTimer: null,
    scriptEl: null,
    jqueryEl: null,
    styleEl: null,
    ownerJavascriptId: javascriptId,
    ownerConfigId: configId,
    ownerMode,
    status: 'idle',
    reinitTimer: null,
    ready,
    resolveReady,
    rejectReady,
    injectDniClasses,
    dniObserver: null,
  };
  setRegistry(entry);
  publishGlobal(entry);
  return entry;
}

function loadScriptEl(
  id: string,
  src: string,
  target: HTMLElement = document.body,
): { el: HTMLScriptElement; promise: Promise<void> } {
  const el = document.createElement('script');
  el.id = id;
  el.src = src;
  el.async = true;
  const promise = new Promise<void>((resolve, reject) => {
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Failed to load ${src}`));
  });
  target.appendChild(el);
  return { el, promise };
}

function injectStyles(entry: RegistryEntry): void {
  if (entry.styleEl || document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = RRAC_STYLE_OVERRIDES;
  document.head.appendChild(style);
  entry.styleEl = style;
}

function startDNIInjectionIfNeeded(entry: RegistryEntry): void {
  if (!entry.injectDniClasses || entry.dniObserver) return;
  // Initial pass before the RRAC <script> is appended, so the script's
  // first DOM scan finds anchors already carrying the dni classes.
  stampDNIClassesIn(document);
  entry.dniObserver = startDNIObserver();
}

function stopDNIInjection(entry: RegistryEntry): void {
  if (entry.dniObserver) {
    entry.dniObserver.disconnect();
    entry.dniObserver = null;
  }
}

async function ensureLoaded(entry: RegistryEntry): Promise<void> {
  if (entry.status !== 'idle') return entry.ready;

  entry.status = 'loading';
  publishGlobal(entry);

  try {
    injectStyles(entry);
    startDNIInjectionIfNeeded(entry);

    if (!(window as any).jQuery) {
      const { el, promise } = loadScriptEl(JQUERY_ID, JQUERY_URL, document.head);
      entry.jqueryEl = el;
      await promise;
    }

    if (!entry.ownerJavascriptId) {
      throw new Error('No javascriptId available');
    }

    const { el, promise } = loadScriptEl(SCRIPT_ID, `${BASE_URL}/${entry.ownerJavascriptId}`);
    entry.scriptEl = el;
    await promise;

    entry.status = 'ready';
    publishGlobal(entry);
    entry.resolveReady();

    requestReinit();
  } catch (err) {
    entry.status = 'error';
    publishGlobal(entry);
    entry.rejectReady(err);
    console.warn('[ApartmentSync] Script load error:', err);
  }
}

function teardown(): void {
  const entry = getRegistry();
  if (!entry || entry.mountCount > 0) return;
  if (entry.consumerCleanupTimer !== null) {
    window.clearTimeout(entry.consumerCleanupTimer);
    entry.consumerCleanupTimer = null;
  }
  if (entry.reinitTimer !== null) {
    window.clearTimeout(entry.reinitTimer);
    entry.reinitTimer = null;
  }
  entry.scriptEl?.remove();
  entry.jqueryEl?.remove();
  entry.styleEl?.remove();
  stopDNIInjection(entry);
  unstampAllDNIMarkers();
  cleanupRRACBodyHelpers();
  setRegistry(null);
  publishGlobal(null);
}

export interface UseRRACScriptOptions {
  javascriptId: string | null;
  configId: string | null;
  role: Role;
  /**
   * Stamp `dniphone` / `dniphonehref` onto every tel:/sms: anchor on
   * the page so RRAC can swap in tracking numbers. Owner-only setting
   * (consumers inherit). Defaults to `false`.
   */
  injectDniClasses?: boolean;
}

export interface UseRRACScriptResult {
  status: Status;
  ready: Promise<void> | null;
  reinit: () => void;
}

export function useRRACScript({
  javascriptId,
  configId,
  role,
  injectDniClasses = false,
}: UseRRACScriptOptions): UseRRACScriptResult {
  const [status, setStatus] = useState<Status>(() => getRegistry()?.status ?? 'idle');

  useEffect(() => {
    if (!javascriptId) {
      setStatus('idle');
      return;
    }

    let cancelled = false;
    let entry = getRegistry();

    if (!entry) {
      entry = createRegistry(
        javascriptId,
        configId,
        role === 'owner' ? 'global' : 'fallback',
        injectDniClasses,
      );
    } else {
      // Owner joining an in-flight fallback session: promote ownership
      // and apply owner-only settings (DNI flag) over the consumer's defaults.
      if (role === 'owner' && entry.ownerMode === 'fallback') {
        entry.ownerMode = 'global';
        publishGlobal(entry);
      }
      if (role === 'owner' && entry.injectDniClasses !== injectDniClasses) {
        entry.injectDniClasses = injectDniClasses;
        if (injectDniClasses) {
          startDNIInjectionIfNeeded(entry);
        } else {
          stopDNIInjection(entry);
        }
      }
      // First mount wins. Mismatched IDs surface as a console warning so
      // misconfigurations are obvious without breaking the page.
      if (entry.ownerJavascriptId && entry.ownerJavascriptId !== javascriptId) {
        console.warn(
          `[ApartmentSync] javascript_id mismatch: this block requested "${javascriptId}" ` +
            `but the page already loaded "${entry.ownerJavascriptId}". Using the existing value. ` +
            `Make sure the apartment-sync-loader and apartment-sync section blocks read from ` +
            `the same custom field.`,
        );
      }
    }

    entry.mountCount += 1;
    if (entry.cleanupTimer !== null) {
      window.clearTimeout(entry.cleanupTimer);
      entry.cleanupTimer = null;
    }
    if (role === 'consumer') {
      entry.consumerCount += 1;
      // Cancel any pending body-helper cleanup — the new consumer wants
      // to keep the existing widget shells alive.
      if (entry.consumerCleanupTimer !== null) {
        window.clearTimeout(entry.consumerCleanupTimer);
        entry.consumerCleanupTimer = null;
      }
    }

    const wasIdle = entry.status === 'idle';
    ensureLoaded(entry).then(() => {
      if (cancelled) return;
      const latest = getRegistry();
      if (!latest) return;
      setStatus(latest.status);
      // Consumers that joined an already-loaded (or in-flight) session need
      // a reinit so RRAC re-scans the DOM and binds to their container.
      // The first loader of a session already calls reinit() inside ensureLoaded.
      if (!wasIdle && role === 'consumer' && latest.status === 'ready') {
        requestReinit();
      }
    });

    setStatus(entry.status);

    return () => {
      cancelled = true;
      const e = getRegistry();
      if (!e) return;
      e.mountCount = Math.max(0, e.mountCount - 1);

      // When the last consumer leaves, drop the vendor's body-level helper
      // shells. RRAC will recreate them via reinit() on the next consumer
      // mount. Multi-consumer pages don't trigger this until BOTH leave.
      // Delayed by CLEANUP_DELAY_MS so StrictMode's synthetic unmount →
      // synchronous remount preserves the helpers (matches the script
      // teardown timer below).
      if (role === 'consumer') {
        e.consumerCount = Math.max(0, e.consumerCount - 1);
        if (e.consumerCount === 0 && e.consumerCleanupTimer === null) {
          e.consumerCleanupTimer = window.setTimeout(() => {
            const latest = getRegistry();
            if (!latest) return;
            latest.consumerCleanupTimer = null;
            if (latest.consumerCount > 0) return;
            cleanupRRACBodyHelpers();
          }, CLEANUP_DELAY_MS);
        }
      }

      if (e.mountCount > 0) return;

      e.cleanupTimer = window.setTimeout(() => {
        const latest = getRegistry();
        if (!latest || latest.mountCount > 0) return;
        teardown();
      }, CLEANUP_DELAY_MS);
    };
  }, [javascriptId, configId, role, injectDniClasses]);

  return {
    status,
    ready: getRegistry()?.ready ?? null,
    reinit: requestReinit,
  };
}
